// src/pages/stock/InventaireEnCours.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    Form, ProgressBar, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function InventaireEnCours() {
    const navigate = useNavigate();
    const [inventaires, setInventaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // États pour les alertes
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchInventaires();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchInventaires = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php?statut=brouillon`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setInventaires(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatutBadge = (statut) => {
        return <Badge bg="warning" className="rounded-pill">Brouillon</Badge>;
    };

    const filteredInventaires = inventaires.filter(i =>
        i.reference_inventaire?.toLowerCase().includes(filterText.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInventaires.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInventaires.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }

        return items;
    };

    // Chargement amélioré (comme les autres pages)
    if (loading && !inventaires.length) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="text-center" style={{ maxWidth: '400px' }}>
                    <div className="loading-ring-wrapper mb-4">
                        <div className="loading-ring"></div>
                        <div className="loading-ring-inner"></div>
                        <i className="ri-loader-4-fill loading-icon"></i>
                    </div>
                    <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                        Chargement des inventaires en cours...
                    </h5>
                    <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                    <div className="mx-auto mt-4" style={{ width: '80%' }}>
                        <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                style={{
                                    width: '100%',
                                    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 1.5s linear infinite'
                                }}
                            />
                        </div>
                    </div>
                    <style>{`
                        .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                        .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                        .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                        .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                        @keyframes spin { to { transform: rotate(360deg); } }
                        @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                    `}</style>
                </div>
            </div>
        );
    }

    if (error && !inventaires.length) {
        return (
            <Alert variant="danger" className="rounded-3 border-0 shadow-sm">
                <div className="d-flex align-items-center">
                    <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                    <div>
                        <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                        <p className="mb-0 small">{error}</p>
                        <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchInventaires}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
            </Alert>
        );
    }

    return (
        <>
            {/* Alertes modernisées */}
            {showSuccessAlert && (
                <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 rounded-3 border-0 shadow-sm alert-modern">
                    <div className="d-flex align-items-center">
                        <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                        <div className="fw-semibold">{successMessage}</div>
                    </div>
                </Alert>
            )}
            {showErrorAlert && (
                <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 rounded-3 border-0 shadow-sm alert-modern">
                    <div className="d-flex align-items-center">
                        <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                        <div className="fw-semibold">{errorMessage}</div>
                    </div>
                </Alert>
            )}

            {/* Filtres avec fond primaryDark */}
            <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
                <Card.Body className="py-3 px-4">
                    <Row className="align-items-center g-3">
                        <Col md={8}>
                            <div className="position-relative">
                                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50"></i>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher un inventaire..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="ps-5 rounded-pill"
                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                />
                            </div>
                        </Col>
                        <Col md={4} className="text-end">
                            <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                <i className="ri-stack-line me-1"></i>
                                {filteredInventaires.length} inventaire(s)
                            </span>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tableau avec header #21ACCD */}
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className="ri-time-line text-white me-2 fs-5"></i>
                            <span className="fw-semibold text-white">Inventaires en cours</span>
                            <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                {filteredInventaires.length} au total
                            </Badge>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Date</th>
                                    <th>Créé par</th>
                                    <th className="text-center">Produits</th>
                                    <th className="text-center">Progression</th>
                                    <th>Statut</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((inv, index) => (
                                        <tr key={inv.id_inventaire} style={{
                                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                        }}>
                                            <td className="ps-4">
                                                <Link to={`/stock/inventaire/${inv.id_inventaire}`} className="table-link">
                                                    {inv.reference_inventaire}
                                                </Link>
                                            </td>
                                            <td>{formatDate(inv.date_inventaire)}</td>
                                            <td>{inv.prenom_utilisateur} {inv.nom_utilisateur}</td>
                                            <td className="text-center">
                                                <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: COLORS.primaryDark }}>
                                                    {inv.nb_produits || 0}
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ minWidth: '120px' }}>
                                                <div className="progress-bar-custom" style={{ width: '100px', margin: '0 auto' }}>
                                                    <div className="progress-fill" style={{ width: `${Math.min((inv.nb_produits_saisis / inv.nb_produits) * 100, 100) || 0}%`, background: '#f59e0b' }}></div>
                                                </div>
                                            </td>
                                            <td>{getStatutBadge(inv.statut)}</td>
                                            <td className="text-center pe-4">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Continuer l'inventaire</Tooltip>}>
                                                        <button
                                                            className="action-icon action-icon-primary"
                                                            onClick={() => navigate(`/stock/inventaire/${inv.id_inventaire}`)}
                                                        >
                                                            <i className="ri-play-line"></i>
                                                        </button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                            <h5 className="mt-3">Aucun inventaire en cours</h5>
                                            <p className="text-muted mb-3">Commencez un nouvel inventaire</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small">
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredInventaires.length)} sur {filteredInventaires.length} inventaires
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {renderPagination()}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Table link */
                .table-link {
                    color: ${COLORS.primary};
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }

                /* Action icons (comme Employes) */
                .action-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-icon:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white;
                }
                .action-icon-primary:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white;
                }

                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* Pagination (comme Employes) */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white;
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                /* Modern alerts */
                .alert-modern {
                    border-left: 4px solid;
                }
                .alert-modern.alert-success {
                    border-left-color: #10b981;
                }
                .alert-modern.alert-danger {
                    border-left-color: #ef4444;
                }

                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
                .text-white-50 {
                    color: rgba(255,255,255,0.5);
                }
            `}</style>
        </>
    );
}